import React, { useEffect, useState } from 'react';
import OwlCarousel from 'react-owl-carousel';
import axios, { AxiosError } from 'axios';
import { useParams } from 'react-router-dom';

// Type definitions
interface Personality {
    id: number;
    name: string;
    profile_picture: string | null;
    wikipedia_url: string | null;
    famous_for: string;
    awards: string | null;
}

interface ApiResponse {
    success: boolean;
    data: Personality[] | null;
}

interface CarouselOptions {
    margin: number;
    responsiveClass: boolean;
    nav: boolean;
    dots: boolean;
    autoplay: boolean;
    smartSpeed: number;
    responsive: {
        [key: number]: {
            items: number;
        };
    };
}

const CareerFamousPersonality: React.FC = () => {
    const [personalities, setPersonalities] = useState<Personality[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const { id } = useParams<{ id: string }>();

    const fallbackImage = `${window.location.origin}/assets/images/yellow-student-boy.svg`;

    useEffect(() => {
        const fetchPersonalities = async (): Promise<void> => {
            try {
                const response = await axios.post<ApiResponse>(
                    `${process.env.REACT_APP_SFE_API}career/fetchFamousPersonality`,
                    {
                        career_id: id
                    },
                    {
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded',
                        }
                    }
                );

                console.log("API response: ", response.data);

                if (response.data.success && response.data.data) {
                    setPersonalities(response.data.data);
                } else {
                    setPersonalities([]);
                }
            } catch (error) {
                console.error("Error fetching personalities:", error);
                const errorMessage = error instanceof AxiosError 
                    ? error.message 
                    : "Failed to load data. Please try again later.";
                setError(errorMessage);
            } finally {
                setLoading(false);
            }
        };

        fetchPersonalities();
    }, [id]);

    const options: CarouselOptions = {
        margin: 20,
        responsiveClass: true,
        nav: true,
        dots: false,
        autoplay: false,
        smartSpeed: 1000,
        responsive: {
            0: { items: 1 },
            400: { items: 1 },
            600: { items: 2 },
            700: { items: 3 },
            1000: { items: 4 },
        },
    };

    console.log("Loading: ", loading);
    console.log("Error: ", error);
    console.log("Personalities: ", personalities);

    if (!loading && !error && personalities.length === 0) {
        console.log("No data, returning null");
        return null;
    }

    return (
        <section className="container-fluid py-5 bg-skyblue position-relative z-0" id="famousPersonalities">
            <div className="max-1140 mx-auto">
                <h1 className="font-32 font-900 mb-3 text-white">Famous Personalities</h1>

                {loading && <p className="text-white">Loading...</p>}
                {error && <p className="text-white">{error}</p>}

                {!loading && !error && personalities.length > 0 && (
                    <OwlCarousel id='FamousPersonalities' className="owl-carousel fourslider" {...options}>
                        {personalities.map((personality) => (
                            <div className="item" key={personality.id}>
                                <div className="guiding_alumni_block">
                                    <div className="guiding_alumni_top">
                                        <div className="guiding_alumni_profile_img">
                                            <img 
                                                src={personality.profile_picture || fallbackImage} 
                                                onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                                                    e.currentTarget.src = fallbackImage;
                                                }} 
                                                width="80" 
                                                alt={personality.name} 
                                            />
                                        </div>
                                        <div className="guiding_alumni_profile_name">
                                            {personality.wikipedia_url ? (
                                                <a href={personality.wikipedia_url} target="_blank" rel="noopener noreferrer">
                                                    {personality.name}
                                                </a>
                                            ) : (
                                                <span>{personality.name}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="guiding_alumni_college_cource">
                                        <ul>
                                            <li><span className="font-500">Famous For:</span> {personality.famous_for}</li>
                                            {personality.awards && personality.awards.length > 0 && (
                                                <li><span className="font-500">Awards:</span> {personality.awards}</li>
                                            )}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </OwlCarousel>
                )}
            </div>
        </section>
    );
};

export default CareerFamousPersonality;